<template>
  <el-dialog
    class="sample-details-dialog"
    visible
    show-close
    @close="abortAction"
  >
    <ContentHeader
      slot="title"
      :title="$t('sampleDetails.title')"
    />
    <div>
      <!-- <ViewActionButtons
        v-if="tabbable"
        :buttons="actionButtons"
      /> -->
      <div
        class="sample-details-index"
        v-for="sample in tableKeysWithSamples"
        :key="sample.tableKey"
      >
        <h2 class="section-title">
          {{ $t('sampleDetails.title') }} - {{ sample.labSampleCode }} -
          {{ sample.productName }}
        </h2>

        <TableWrapper
          :table-columns="sampleDetailsColumns"
          :show-pagination="false"
          :table-key="sample.tableKey"
          height="300"
          row-key="tableId"
          @insertMeasuredValue="sampleDetailsMixin.openInsertMeasuredValue"
          @insertMeasuredValueBulk="
            sampleDetailsMixin.openInsertMeasuredValueBulk
          "
          @createTemperatureTrail="
            sampleDetailsMixin.openCreateTemperatureTrail
          "
          @enableRawData="sampleDetailsMixin.enableDisableRawData"
          @disableRawData="sampleDetailsMixin.enableDisableRawData"
          @associateLocation="openDialogLocation"
          @cancelAnalysis="cancelAnalysis"
        />
      </div>
    </div>

    <insertMeasuredValues
      v-if="
        measuredValueModal &&
        selectedOperationId &&
        selectedSampleAnalysisId &&
        selectedSample
      "
      :lab-sample-analysis-id="selectedSampleAnalysisId"
      :operationId="selectedOperationId"
      :current-values="currentValues"
      :currentOperation="currentOperation"
      :sample="selectedSample"
      append-to-body
      @dialogClose="sampleDetailsMixin.handleDialogClose"
    />

    <insertMeasuredValuesBulk
      v-if="measuredValueModalBulk && selectedSample && selectedAnalysis"
      :sample="selectedSample"
      :analysis="selectedAnalysis"
      append-to-body
      @dialogClose="sampleDetailsMixin.handleDialogClose"
    />

    <CreateTemperatureTrail
      v-if="temperatureTrailModal && temperatureTrailAnalysisIds"
      :labSampleAnalysisIds="temperatureTrailAnalysisIds"
      append-to-body
      @dialogClose="sampleDetailsMixin.handleDialogClose"
    />

    <AssociateLocation
      v-if="openLocationModal"
      :sample="selectedSample"
      append-to-body
      @dialogClose="closeDialogLocation"
    />
  </el-dialog>
</template>

<script>
  import { mapActions } from 'vuex';

  import { samplesApi, analysesDataApi } from '@/api';
  import { sampleDetailsColumns } from '@/config';
  import sampleDetailsMixin from '@/mixins/sampleDetails';

  import { ANALYSIS_ACTIONS } from '@/config/constants';

  import TableWrapper from '@/components/TableWrapper.vue';
  import ContentHeader from '@/components/ContentHeader.vue';
  // import ViewActionButtons from '@/components/ViewActionButtons.vue';
  import insertMeasuredValues from './insertMeasuredValues.vue';
  import InsertMeasuredValuesBulk from './insertMeasuredValuesBulk.vue';
  import CreateTemperatureTrail from './CreateTemperatureTrail.vue';
  import AssociateLocation from './AssociateLocation.vue';

  export default {
    name: 'SampleDetailsModal',
    components: {
      ContentHeader,
      TableWrapper,
      // ViewActionButtons,
      insertMeasuredValues,
      InsertMeasuredValuesBulk,
      CreateTemperatureTrail,
      AssociateLocation,
    },
    props: {
      sampleIds: { type: Array, required: true },
      tabbable: { type: Boolean, default: false },
      tabKey: { type: String },
      tabLabel: { type: String },
    },
    data() {
      return {
        // Globals
        sampleDetailsColumns,
        tableKeysWithSamples: [],
        actionButtons: [
          {
            text: this.$t('global.openAsTab'),
            type: 'secondary',
            action: this.addModalToTabs,
          },
        ],

        // Insert Measured Values
        selectedSampleAnalysisId: null,
        selectedOperationId: null,
        currentValues: null,
        currentOperation: null,
        measuredValueModal: false,

        // Insert Measured Values Bulk
        measuredValueModalBulk: false,
        selectedSample: null,
        selectedAnalysis: null,

        // Temperature trail
        temperatureTrailModal: false,
        temperatureTrailAnalysisIds: null,

        // Associate Location
        openLocationModal: false,
      };
    },
    created() {
      this.sampleDetailsMixin = sampleDetailsMixin(this);
      this.tableRows();
    },
    methods: {
      ...mapActions(['updateTableRows', 'addTable', 'removeTable', 'addTab']),
      async tableRows() {
        try {
          const indexResponse = await samplesApi.samplesAnalysesMultiple(
            this.sampleIds
          );
          this.sampleDetailsMixin.prepareTableData(indexResponse);
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      addModalToTabs() {
        const routeName = 'sampleDetails';
        const query = { sampleIds: this.sampleIds };
        const params = { sampleId: this.tabKey };
        const label =
          this.tableKeysWithSamples.length === 1
            ? `${this.$t('sampleDetails.tabLabel')} - ${
                this.tableKeysWithSamples[0].productName
              }`
            : this.tabLabel;
        this.addTab({
          name: `sampleDetails${this.tabKey}`,
          routeName,
          path: '',
          query,
          params,
          meta: { label },
        });
        this.$router.push({ name: routeName, query, params });
      },
      abortAction() {
        this.tableKeysWithSamples.forEach((key) => {
          this.removeTable({ tableKey: key.tableKey });
        });
        this.$emit('dialogClose');
      },
      openDialogLocation(row) {
        this.selectedSample = row;
        if (row.parentId) {
          const confirmMessage = this.$t('sampleDetails.locationClassWarning');
          this.$confirm(confirmMessage, this.$t('global.warning'), {
            confirmButtonText: this.$t('global.confirm'),
            cancelButtonText: this.$t('global.back'),
          })
            .then(() => {
              this.openLocationModal = true;
            })
            .catch(() => {
              this.openLocationModal = false;
            });
        } else {
          this.openLocationModal = true;
        }
      },
      closeDialogLocation() {
        this.selectedSample = null;
        this.openLocationModal = false;
      },
      cancelAnalysis(row) {
        let confirmMessage = this.$t('global.confirmAction');
        if (row.parentId)
          confirmMessage = this.$t('sampleDetails.cancelAnalysisWithParent', {
            code: row.code,
          });
        this.$confirm(confirmMessage, this.$t('global.warning'), {
          confirmButtonText: this.$t('global.confirm'),
          cancelButtonText: this.$t('global.back'),
        })
          .then(async () => {
            await analysesDataApi.laboratorySampleAnalysesAction(
              row.labSampleAnalysisId,
              {
                action: ANALYSIS_ACTIONS.CANCEL,
              }
            );
            await this.tableRows();
          })
          .catch((error) => {
            if (error.response) {
              this.$notify({
                showClose: true,
                type: 'error',
                message: this.$t('sampleDetails.cancelAnalysisError'),
              });
            }
          });
      },
    },
  };
</script>

<style lang="scss">
  .sample-details-dialog {
    .el-dialog {
      width: 95%;
    }

    .section-title {
      margin: 30px 0;
    }

    .table-cell-labSampleAnalysisStatus {
      position: relative;

      @each $modifier, $map in $sample-analysis-statuses {
        &--#{$modifier} {
          & .cell {
            padding-left: 20px;
          }

          &:after {
            content: '';
            display: block;
            width: 10px;
            position: absolute;
            height: 100%;
            top: 0;
            left: 0;
            background: map-get($map, 'background');
          }
        }
      }
    }
  }
</style>
